import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

const services = [
  { name: 'Software Development', desc: 'Web, Desktop & Mobile Apps' },
  { name: 'Web & Email Services', desc: 'Hosting, Domain & Email' },
  { name: 'Microsoft Cloud', desc: 'Azure, M365 & Dynamics 365' },
  { name: 'IT Consulting', desc: 'Security, Audits & Support' },
  { name: 'Business Systems', desc: 'CRM, ERP & HRMS' },
];

const navLinks = ['About', 'Clients', 'Partners', 'Contact'];
const publicUrl = process.env.PUBLIC_URL || '';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          padding: scrolled ? '0.75rem 2rem' : '1.25rem 2rem',
          background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--gray-light)' : 'none',
          transition: 'all 0.4s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        {/* Logo */}
        <a href="#" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <img src={`${publicUrl}/logo192.png`} alt="Findura Infosys" style={{ width: 50, height: 50, borderRadius: 15 }} />
          <span style={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em', color: scrolled ? 'var(--text)' : 'white', transition: 'color 0.4s ease' }}>
            Findura <span style={{ color: 'var(--orange)' }}>Infosys Limited</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} className="desktop-nav">
          {/* Services mega menu trigger */}
          <div
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
            style={{ position: 'relative' }}
          >
            <button
              style={{
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.5rem 1rem', borderRadius: 8,
                color: servicesOpen ? 'var(--orange)' : (scrolled ? 'var(--text)' : 'white'),
                fontSize: '0.9rem', fontWeight: 500,
                background: servicesOpen ? 'var(--electric-dim)' : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              Services <ChevronDown size={14} style={{ transform: servicesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
            </button>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: '-1rem',
                    width: 520, padding: '1.5rem',
                    background: 'rgba(255, 255, 255, 0.97)',
                    border: '1px solid var(--gray-light)',
                    borderRadius: 16,
                    backdropFilter: 'blur(30px)',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.1), 0 0 0 1px rgba(255, 107, 0, 0.1)',
                  }}
                >
                  <p style={{ fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '1rem', fontWeight: 600 }}>Our Services</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {services.map((s, i) => (
                      <motion.a
                        key={i}
                        href="#services"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        style={{
                          padding: '0.75rem 1rem', borderRadius: 10,
                          border: '1px solid transparent',
                          transition: 'all 0.2s',
                          display: 'block',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.background = 'var(--electric-dim)';
                          (e.currentTarget as HTMLElement).style.borderColor = 'var(--gray-light)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                          (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
                        }}
                      >
                        <div style={{ fontFamily: 'Syne', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.2rem' }}>{s.name}</div>
                        <div style={{ fontSize: '0.775rem', color: 'var(--text-soft)' }}>{s.desc}</div>
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.map(link => (
            <a
              key={link}
              href={link === 'About' ? '#/about' : `#${link.toLowerCase()}`}
              style={{
                padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.9rem', fontWeight: 500,
                color: scrolled ? 'var(--text)' : 'white', transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.color = 'var(--orange)';
                (e.currentTarget as HTMLElement).style.background = 'var(--electric-dim)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.color = scrolled ? 'var(--text)' : 'white';
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >{link}</a>
          ))}

          <a
            href="#contact"
            style={{
              marginLeft: '0.5rem', padding: '0.6rem 1.4rem', borderRadius: 10,
              background: 'var(--orange)', color: 'var(--white)',
              fontWeight: 700, fontSize: '0.875rem', fontFamily: 'Syne',
              boxShadow: '0 0 20px var(--electric-glow)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px var(--electric-glow)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px var(--electric-glow)'; }}
          >Get in Touch</a>
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)} style={{ color: scrolled ? 'var(--text)' : 'white', display: 'none', transition: 'color 0.4s ease' }} className="mobile-menu-btn">
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed', inset: 0, zIndex: 99,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              display: 'flex', flexDirection: 'column',
              padding: '6rem 2rem 2rem',
              gap: '0.5rem',
            }}
          >
            {['Services', ...navLinks].map((link, i) => (
              <motion.a
                key={link}
                href={`#${link.toLowerCase()}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                onClick={() => setMenuOpen(false)}
                style={{
                  padding: '1rem 1.5rem', borderRadius: 12,
                  fontFamily: 'Syne', fontWeight: 600, fontSize: '1.1rem',
                  color: 'var(--text)',
                  border: '1px solid var(--gray-light)',
                }}
              >{link}</motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </>
  );
}
